import PropTypes from "prop-types";
import classNames from "classnames";
import FloatingButton from "app/pages/.shared/Popover/FloatingButton";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import IconTag from "app/pages/.shared/static/icons/IconTag";
import AmountContainer from "app/pages/.shared/AmountContainer";
import Separator from "app/pages/.shared/Separator";
import { useIntl } from "react-intl";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import "./SavingsHighlight.scss";

const SavingsHighlight = ({
	message,
	amount,
	description,
	className,
	showDescriptionAmount = false,
	descriptionAmountLabel,
	trackEvent,
	trackData,
}) => {
	const intl = useIntl();
	const { track } = useAnalytics();
	const popoverClassName = classNames("savings-highlight__popover", className);

	// Track on click with the provided event name
	const handleClick = trackEvent ? () => track(trackEvent, trackData) : undefined;

	// Track on hover: auto-generates event name by replacing "_click_" with "_hover_"
	const handleHover = trackEvent
		? () => track(trackEvent.replace("_click_", "_hover_"), trackData)
		: undefined;

	let tooltipBody = null;
	// Arrays -> bullet list in tooltip
	if (Array.isArray(description) && description.filter(Boolean).length) {
		tooltipBody = (
			<Typography variant={TYPOGRAPHY_VARIANTS.SMALL} component="div">
				<ul className="savings-highlight__list">
					{description.filter(Boolean).map((line, index) => (
						<li key={index}>{line}</li>
					))}
				</ul>
			</Typography>
		);
		// Single value -> plain text in tooltip
	} else if (!Array.isArray(description) && Boolean(description)) {
		tooltipBody = <Typography variant={TYPOGRAPHY_VARIANTS.SMALL}>{description}</Typography>;
	}

	const referenceContent = (
		<Typography
			variant={TYPOGRAPHY_VARIANTS.SMALL}
			className="savings-highlight__content"
			onClick={handleClick}
			onMouseEnter={handleHover}
		>
			{message}
		</Typography>
	);

	// No description -> display static label only
	if (!tooltipBody) {
		return (
			<div className="savings-highlight">
				<IconTag className="savings-highlight__icon" />
				{referenceContent}
			</div>
		);
	}

	const footerLabel =
		descriptionAmountLabel ?? intl.formatMessage({ id: "savings.tooltip.amount.label" });

	return (
		<div className="savings-highlight">
			<IconTag className="savings-highlight__icon" />
			<FloatingButton
				position="bottom"
				referenceComponent={referenceContent}
				zIndexValue={100}
				floatingContent={
					<div className="savings-highlight__description-content">
						{tooltipBody}
						{showDescriptionAmount && (
							<>
								<div className="savings-highlight__separator">
									<Separator design="full" margin="narrow" />
								</div>
								<Typography
									variant={TYPOGRAPHY_VARIANTS.REGULAR}
									component="div"
									className="savings-highlight__description-footer"
								>
									<div className="savings-highlight__description-label">
										{footerLabel}
									</div>
									<AmountContainer amount={amount} />
								</Typography>
							</>
						)}
					</div>
				}
				popoverClassName={popoverClassName}
				dismissOnFloatingClick
				trigger="hover"
			/>
		</div>
	);
};

SavingsHighlight.propTypes = {
	message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
	amount: PropTypes.number,
	description: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.node,
		PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.node])),
	]),
	className: PropTypes.string,
	showDescriptionAmount: PropTypes.bool,
	descriptionAmountLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
	trackEvent: PropTypes.string,
	trackData: PropTypes.object,
};

export default SavingsHighlight;
